/**
 * useRealtimeDashboard Hook
 * Provides real-time WebSocket updates for dashboard widgets
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useRealtimeDashboard = (userId, onUpdate) => {
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    if (!userId) return;
    
    try {
      // Construct WebSocket URL
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = API_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const wsUrl = `${wsProtocol}//${wsHost}/api/realtime/ws/dashboard?user_id=${userId}`;
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Dashboard WebSocket connected');
        setConnected(true);
        reconnectAttempts.current = 0;
        
        // Send subscription message
        ws.send(JSON.stringify({
          type: 'subscribe',
          channels: ['dashboard_stats', 'patient_alerts', 'compliance_updates', 'visit_updates']
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastUpdate(new Date().toISOString());
          
          // Handle different message types
          switch (data.type) {
            case 'stats_update':
              if (onUpdate) onUpdate('stats', data.payload);
              break;
            case 'patient_alert':
              if (onUpdate) onUpdate('alert', data.payload);
              // Show toast for critical alerts
              if (data.payload?.priority === 'critical') {
                toast.error(data.payload.message || 'Critical Alert', {
                  description: data.payload.patient_name,
                  duration: 10000
                });
              }
              break;
            case 'compliance_update':
              if (onUpdate) onUpdate('compliance', data.payload);
              break;
            case 'visit_update':
              if (onUpdate) onUpdate('visit', data.payload);
              break;
            case 'ping':
              ws.send(JSON.stringify({ type: 'pong' }));
              break;
            default:
              console.log('Unknown message type:', data.type);
          }
        } catch (error) {
          console.warn('Failed to parse WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('Dashboard WebSocket disconnected', event.code);
        setConnected(false);
        wsRef.current = null;

        // Attempt reconnection
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.warn('Dashboard WebSocket error:', error);
      };

    } catch (error) {
      console.warn('Failed to connect WebSocket:', error);
    }
  }, [userId, onUpdate]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Connect on mount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    connected,
    lastUpdate,
    sendMessage,
    reconnect: connect,
    disconnect
  };
};

export default useRealtimeDashboard;
